import { useEffect, useRef, useState } from 'react';

import { CircleAlertIcon, FileTextIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import GoogleDrive from '@/components/atoms/Icons/GoogleDrive';
import {
  SidebarWrapperContent,
  SidebarWrapperFooter,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import useDatasources from '@/hooks/useDatasources';
import { openPublishModal } from '@/redux/collections/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import {
  DatasourceStatus,
  DatasourceType,
  GoogleDriveConfig,
  GoogleDrivePickedFile,
} from '@/shared/types/collections';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { noop, useHasDirtyFields } from '@/util/util';
import { LENGTH_XS } from '@/util/validation/constants';

import {
  buildGoogleDriveDatasource,
  readableFiles,
  canSaveGoogleDrive,
  exchangeAuthorizationCode,
  GoogleDriveFormValues,
  GoogleDriveTokens,
  isGoogleDriveConfig,
  loadPicker,
  mergeFiles,
  pickFiles,
  requestAuthorizationCode,
  useGoogleDriveSettings,
  useSignInScript,
} from './googleDrive';
import { EXTERNAL_SOURCE_FIELD, EXTERNAL_SOURCE_FOOTER, EXTERNAL_SOURCE_SECTION } from './layout';
import { useExternalSourceDatasourceId } from './useExternalSourceDatasourceId';

type FormValues = GoogleDriveFormValues & { keep_published?: boolean };

type GoogleDriveFormProps = { handleCancel: (withValidation?: boolean) => void };

const DIRTY = { shouldDirty: true };

export const GoogleDriveForm = ({ handleCancel }: GoogleDriveFormProps) => {
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );
  const dispatch = useDispatch();
  const { collectionId } = useParams();
  const { t } = useTranslation();
  const form = useFormContext<FormValues>();
  const datasourceId = useExternalSourceDatasourceId();
  const { datasource, getStatus, createDatasourceAsync, updateDatasourceAsync } = useDatasources(
    collectionId,
    datasourceId
  );
  const { data: settings, isError: isSettingsError } = useGoogleDriveSettings();
  const isEnabled = !!settings?.enabled;
  const { data: isSignInLoaded } = useSignInScript(canWrite && isEnabled);
  const [isConnecting, setIsConnecting] = useState(false);
  const canConnect = canWrite && isEnabled && !!isSignInLoaded && !isConnecting;
  const initializedRef = useRef(false);

  const editing =
    !!datasourceId && getStatus === 'success' && datasource.type === DatasourceType.GDRIVE
      ? datasource
      : undefined;
  const isInvalid = editing?.status === DatasourceStatus.INVALID;
  const showKeepPublished = !!editing && !editing.is_draft && !isInvalid;
  const isNotConfigured = !!editing && (isSettingsError || settings?.enabled === false);

  const [name, refreshToken, files] = useWatch({
    control: form.control,
    name: ['name', 'refresh_token', 'files'],
  });
  const { isSubmitting } = useFormState({ control: form.control });
  const isDirty = useHasDirtyFields(form.control);
  const canSave =
    canWrite &&
    !isConnecting &&
    canSaveGoogleDrive({ name, refresh_token: refreshToken, files }, !!editing) &&
    (!editing || isDirty);

  useEffect(() => {
    if (initializedRef.current || !editing) {
      return;
    }
    initializedRef.current = true;
    form.resetField('name', { defaultValue: editing.name });
    form.resetField('keep_published', { defaultValue: true });
    if (isGoogleDriveConfig(editing.config)) {
      form.resetField('files', { defaultValue: editing.config.files });
    }
  }, [editing, form]);

  const authorize = async (): Promise<GoogleDriveTokens | null> => {
    if (!settings?.enabled) {
      return null;
    }
    const code = await requestAuthorizationCode(settings.client_id);
    if (!code) {
      return null;
    }
    return exchangeAuthorizationCode(code);
  };

  const withConnecting = async (action: () => Promise<void>) => {
    setIsConnecting(true);
    try {
      await action();
    } catch {
      toast.error(t('collections.google_drive.connect_error'));
    } finally {
      setIsConnecting(false);
    }
  };

  const handleChooseFiles = () =>
    withConnecting(async () => {
      loadPicker().catch(noop);
      const tokens = await authorize();
      if (!tokens || !settings?.enabled) {
        return;
      }
      const picked = await pickFiles(settings, tokens.access_token);
      if (!picked) {
        return;
      }
      form.setValue('refresh_token', tokens.refresh_token, DIRTY);
      form.setValue('files', mergeFiles([], picked), DIRTY);
      if (!form.getValues('name')) {
        form.setValue('name', picked[0].name.trim().slice(0, LENGTH_XS), {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    });

  const handleAddFiles = () =>
    withConnecting(async () => {
      loadPicker().catch(noop);
      const tokens = await authorize();
      if (!tokens || !settings?.enabled) {
        return;
      }
      const picked = await pickFiles(settings, tokens.access_token);
      if (!picked) {
        return;
      }
      form.setValue('refresh_token', tokens.refresh_token, DIRTY);
      form.setValue('files', mergeFiles(form.getValues('files'), picked), DIRTY);
    });

  const removeFile = (id: string) =>
    form.setValue(
      'files',
      (form.getValues('files') ?? []).filter((file) => file.id !== id),
      DIRTY
    );

  const findReadableFiles = async (
    config: GoogleDriveConfig,
    accessToken: string
  ): Promise<GoogleDrivePickedFile[] | null> => {
    const readable = await readableFiles(config.files, accessToken);
    if (readable.length && readable.length === config.files.length) {
      return config.files;
    }
    if (!settings?.enabled) {
      return null;
    }
    const picked = await pickFiles(settings, accessToken);
    return picked && mergeFiles(readable, picked);
  };

  const handleReconnect = () =>
    withConnecting(async () => {
      if (!editing || !isGoogleDriveConfig(editing.config)) {
        return;
      }
      const tokens = await authorize();
      if (!tokens) {
        return;
      }
      const readable = await findReadableFiles(editing.config, tokens.access_token);
      if (!readable) {
        return;
      }
      try {
        await updateDatasourceAsync({
          datasource_id: editing.datasource_id,
          type: DatasourceType.GDRIVE,
          name: editing.name,
          config: { files: readable, refresh_token: tokens.refresh_token },
        });
      } catch {
        // The mutation shows its own error toast; the connect error would be wrong here.
        return;
      }
      form.resetField('files', { defaultValue: readable });
    });

  const onSubmit = async (data: FormValues) => {
    const payload = buildGoogleDriveDatasource(data);

    if (editing) {
      await updateDatasourceAsync({
        datasource_id: editing.datasource_id,
        ...payload,
        ...(showKeepPublished && { is_draft: !data.keep_published }),
      });
      handleCancel();
      return;
    }

    await createDatasourceAsync(payload);
    toast(t('collections.toast_messages.external_source_uploaded'), {
      description: t('collections.toast_messages.external_source_draft'),
      action: { label: t('collections.publish'), onClick: () => dispatch(openPublishModal()) },
    });
    handleCancel();
  };

  return (
    <>
      <SidebarWrapperContent className={EXTERNAL_SOURCE_SECTION}>
        {isInvalid && (
          <Alert variant="destructive">
            <CircleAlertIcon />
            <AlertTitle>{t('collections.google_drive.invalid_title')}</AlertTitle>
            <AlertDescription className="flex flex-col items-start gap-3">
              {t('collections.google_drive.invalid_description')}
              <Button
                type="button"
                size="sm"
                variant="outline-neutral"
                onClick={handleReconnect}
                isLoading={isConnecting}
                disabled={!canConnect}
              >
                {t('collections.google_drive.reconnect')}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <FormField
          control={form.control}
          name="name"
          render={({ field, fieldState }) => (
            <FormItem className={EXTERNAL_SOURCE_FIELD}>
              <FormLabel>{t('common.name')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  value={field.value ?? ''}
                  onBlur={() => {
                    field.onBlur();
                    form.trigger('name');
                  }}
                  onChange={(e) => {
                    field.onChange(e);
                    if (fieldState.isTouched) {
                      form.trigger('name');
                    }
                  }}
                  placeholder={t('common.name_placeholder')}
                  disabled={!canWrite}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="files"
          render={({ field }) => (
            <FormItem className={EXTERNAL_SOURCE_FIELD}>
              <div className="flex w-full flex-col gap-0.5">
                <FormLabel className="mb-0 text-foreground">
                  {t('collections.google_drive.documents')}
                </FormLabel>
                <p className="text-sm leading-5 text-muted-foreground">
                  {t('collections.google_drive.connect_description')}
                </p>
              </div>
              {isNotConfigured && (
                <p className="text-sm leading-5 text-muted-foreground">
                  {t('collections.google_drive.not_configured')}
                </p>
              )}
              {field.value?.length ? (
                <>
                  <ul className="flex max-h-40 w-full flex-col gap-1 overflow-y-auto">
                    {field.value.map((file) => (
                      <li
                        key={file.id}
                        className="group flex items-center gap-2 rounded p-1 hover:bg-accent"
                      >
                        <div className="flex min-w-0 flex-1 items-center gap-1">
                          <FileTextIcon size={16} className="shrink-0 text-muted-foreground" />
                          <span
                            className="truncate text-xs leading-4 font-medium text-muted-foreground"
                            title={file.name}
                          >
                            {file.name}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="shrink-0 rounded-sm text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed"
                          onClick={() => removeFile(file.id)}
                          disabled={!canWrite || isConnecting}
                          aria-label={t('collections.google_drive.remove_document', {
                            0: file.name,
                          })}
                        >
                          <Trash2Icon size={14} />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <Button
                    type="button"
                    variant="ghost-neutral"
                    size="sm"
                    onClick={handleAddFiles}
                    isLoading={isConnecting}
                    disabled={!canConnect}
                  >
                    <PlusIcon />
                    {t('collections.google_drive.add_documents')}
                  </Button>
                </>
              ) : (
                <Button
                  type="button"
                  variant="outline-neutral"
                  onClick={handleChooseFiles}
                  isLoading={isConnecting}
                  disabled={!canConnect}
                >
                  <GoogleDrive size={16} />
                  {t('collections.google_drive.connect')}
                </Button>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        {showKeepPublished && (
          <FormField
            control={form.control}
            name="keep_published"
            render={({ field }) => (
              <FormItem className="flex gap-2 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={!canWrite}
                  />
                </FormControl>
                <FormLabel className="flex flex-col items-start gap-1 leading-none">
                  {t('collections.add_faq.keep_published')}
                  <FormDescription>
                    {t('collections.add_faq.keep_published_description')}
                  </FormDescription>
                </FormLabel>
              </FormItem>
            )}
          />
        )}
      </SidebarWrapperContent>

      <SidebarWrapperFooter className={EXTERNAL_SOURCE_FOOTER}>
        <Button type="button" variant="ghost-neutral" onClick={() => handleCancel(true)}>
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          onClick={form.handleSubmit(onSubmit)}
          disabled={!canSave}
          isLoading={isSubmitting}
        >
          {editing ? t('common.update') : t('common.add')}
        </Button>
      </SidebarWrapperFooter>
    </>
  );
};
